// components
import { Box, Switch, Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { IconButton } from '@sdb/planning-ui';
import {
  StyledActionListItemIcon,
  StyledActionListItemText,
  StyledListSubheader,
  StyledSecondaryTypography,
  StyledToolbarActionMenuBox,
  StyledToolbarActionMenuPaper
} from './ToolbarActionMenu.styles';

// enums
import { ToolbarPositionEnum } from '../ToolbarWrapper.enums';

// hooks
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import { StyledBoxFlexAlignCenter } from 'components/shared/Box/Box.styles';

// types
import type { ToolbarActionMenuProps } from './ToolbarActionMenu.types';
import type { ToolbarContextMenuItem } from '../ToolbarSet.types';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

export default function ToolbarActionMenu({
  action,
  additionalActions,
  isAdditionalActions = false,
  setToolbarPosition,
  isRightPositioned
}: ToolbarActionMenuProps) {
  const { t } = useTranslation('common', { keyPrefix: 'toolbar' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const defaultToolbarPositionActions: ToolbarContextMenuItem[] = [
    {
      groupTitle: t('kit'),
      label: t('positionRight'),
      icon: 'arrow-right',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.RIGHT)
    },
    {
      label: t('positionTop'),
      icon: 'arrow-up',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.TOP)
    },
    {
      label: t('zenMode'),
      icon: 'wheat',
      onClick: () => setToolbarPosition?.(ToolbarPositionEnum.ZEN)
    }
  ];

  const moreActions = additionalActions ? [...additionalActions] : [];
  const menuListActions = isAdditionalActions
    ? [...moreActions, ...defaultToolbarPositionActions]
    : action?.contextMenu;

  return (
    <>
      <Tooltip
        title={
          isAdditionalActions
            ? t('others', { keyPrefix: '' })
            : action?.tooltipLabel
        }
        placement={isRightPositioned ? 'left' : 'bottom'}
      >
        <IconButton
          type={
            (isAdditionalActions
              ? 'ellipsis-stroke-vertical'
              : action?.icon) as IconType
          }
          size="small"
          onClick={handleClick}
          aria-controls={open ? action?.id : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          data-tourid={action?.actionDataTourId}
        />
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        id={action?.id}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: StyledToolbarActionMenuPaper
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuList>
          {menuListActions?.map((action, index) => (
            <Box key={index}>
              {action.groupTitle && (
                <StyledListSubheader>{action.groupTitle}</StyledListSubheader>
              )}

              {action.isCheckable ? (
                <StyledToolbarActionMenuBox>
                  <StyledBoxFlexAlignCenter>
                    {action.icon && (
                      <StyledActionListItemIcon>
                        <IconButton
                          type={action.icon}
                          size="small"
                          color="inherit"
                          disabled={action.disabled}
                        />
                      </StyledActionListItemIcon>
                    )}
                    <StyledActionListItemText>
                      {action.label}
                    </StyledActionListItemText>
                  </StyledBoxFlexAlignCenter>
                  <Switch
                    checked={!!action.selected}
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick?.();
                    }}
                  />
                </StyledToolbarActionMenuBox>
              ) : (
                <MenuItem
                  onClick={() => {
                    action.onClick?.();
                    handleClose();
                  }}
                  selected={action.selected}
                >
                  {action.icon && (
                    <StyledActionListItemIcon>
                      <IconButton
                        type={action.icon}
                        size="small"
                        color="inherit"
                        disabled={action.disabled}
                      />
                    </StyledActionListItemIcon>
                  )}

                  <ListItemText>{action.label}</ListItemText>

                  {action?.shortcut && (
                    <StyledSecondaryTypography variant="body2">
                      {action?.shortcut}
                    </StyledSecondaryTypography>
                  )}
                </MenuItem>
              )}

              {action.dividerAfter && <Divider />}
            </Box>
          ))}
        </MenuList>
      </Menu>
    </>
  );
}
